import { type SecretaryService } from './SecretaryService'
import { type SecretaryType } from '../../model/secretary/Secretary'
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc
} from 'firebase/firestore'
import { db, functions } from '../../db'
import { httpsCallable } from 'firebase/functions'

export class SecretaryServiceImpl implements SecretaryService {
  private static readonly collectionName = 'secretary'

  async createSecretary(input: {
    email: string
    name: string
  }): Promise<SecretaryType> {
    const create = httpsCallable<typeof input, SecretaryType>(
      functions,
      'createSecretary'
    )
    const result = await create(input)
    return result.data
  }

  async getSecretaryByUid(uid: string): Promise<SecretaryType | undefined> {
    const docRef = doc(db, SecretaryServiceImpl.collectionName, uid)
    const docSnap = await getDoc(docRef)
    if (docSnap.exists()) {
      const data = docSnap.data() as Omit<SecretaryType, 'uid'>
      return { ...data, uid: docSnap.id, status: data.status ?? 'active' }
    }
    return undefined
  }

  async getSecretaryByDoctorId(
    doctorId: string
  ): Promise<SecretaryType | undefined> {
    const secretaries = await this.getSecretariesByDoctorId(doctorId)
    return secretaries.find((secretary) => secretary.status !== 'disabled')
  }

  async getSecretariesByDoctorId(doctorId: string): Promise<SecretaryType[]> {
    const q = query(
      collection(db, SecretaryServiceImpl.collectionName),
      where('doctorId', '==', doctorId)
    )
    const snapshot = await getDocs(q)
    return snapshot.docs.map((docSnap) => {
      const data = docSnap.data() as Omit<SecretaryType, 'uid'>
      return {
        uid: docSnap.id,
        ...data,
        status: data.status ?? 'active'
      }
    })
  }

  async deleteSecretary(uid: string): Promise<void> {
    const remove = httpsCallable<{ uid: string }, { ok: boolean }>(
      functions,
      'deleteSecretary'
    )
    await remove({ uid })
  }

  async activateSecretary(): Promise<void> {
    const activate = httpsCallable<Record<string, never>, { ok: boolean }>(
      functions,
      'activateSecretary'
    )
    await activate({})
  }
}
